import { IDate, QueryKeys } from '@/types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import Big from 'big.js';
import { BotInfo, BrowserInfo, detect, NodeInfo, ReactNativeInfo, SearchBotDeviceInfo } from 'detect-browser';
import { dateDiff, IDropdownOption, now, nowInstant, parseDate } from 'shared-components';
import {
  AccommodationTypeStatusEnum,
  ApplicantOfferResponseStatus,
  IImportData,
  IImportOffer,
  IPathwayId,
  IRecordedInterview,
  RecordedInterviewSystemStatus,
  Workflows,
} from 'api-paths-cas';
import { UNI_ROLES, UserRole } from '../store/auth/types';
import { DEFAULT_NOT_INVITES_STAGE_TAB, DEFAULT_STAGE_TAB } from './constants';
import { normalizeToFullYear } from './formatting';

const MIN_EDGE_VERSION = 16;
const MIN_EDGE_CHROMIUM_VERSION = 79;
const MIN_CHROME_VERSION = 57;
const MIN_FIREFOX_VERSION = 52;
const MIN_SAFARI_VERSION = 10.1;
const MIN_OPERA_VERSION = 44;
const MIN_CRIOS_VERSION = 91;

const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const NANOSECONDS_PER_SECOND = 1_000_000_000;
const MINUTES_THRESHOLD = 60;
const HOURS_THRESHOLD = 24;
const DAYS_THRESHOLD = 21;
const DAYS_PER_WEEK = 7;

export const isVersionSupported = (browserName: string, browserVersion: string): boolean => {
  const versionWithSingleDecimalArr = browserVersion ? browserVersion.split('.') : [];
  const versionWithSingleDecimal =
    versionWithSingleDecimalArr.length > 1
      ? `${versionWithSingleDecimalArr[0]}.${versionWithSingleDecimalArr[1]}`
      : `${versionWithSingleDecimalArr[0]}`;

  const version = new Big(versionWithSingleDecimal);

  switch (browserName) {
    case 'edge': {
      return version.gte(MIN_EDGE_VERSION);
    }
    case 'edge-chromium': {
      return version.gte(MIN_EDGE_CHROMIUM_VERSION);
    }
    // case 'samsung': {
    // 	return version.gte(7);
    // }
    case 'chrome': {
      return version.gte(MIN_CHROME_VERSION);
    }
    case 'firefox': {
      return version.gte(MIN_FIREFOX_VERSION);
    }
    // case 'android': {
    // 	return version.gte(67);
    // }
    // case 'ios': {
    // 	return version.gte(10.3);
    // }
    case 'safari': {
      return version.gte(MIN_SAFARI_VERSION);
    }
    case 'opera': {
      return version.gte(MIN_OPERA_VERSION);
    }
    case 'crios': {
      return version.gte(MIN_CRIOS_VERSION);
    }
  }
  return false;
};

export const isBrowserSupported = (): boolean => {
  const browser: BrowserInfo | BotInfo | NodeInfo | SearchBotDeviceInfo | ReactNativeInfo | null = detect();
  const supportedBrowsers = [
    'edge',
    'edge-chromium',
    'samsung',
    'chrome',
    'firefox',
    'android',
    'ios',
    'safari',
    'opera',
    'crios',
  ];

  return browser
    ? supportedBrowsers.includes(browser.name) &&
        isVersionSupported(browser.name, browser.version ? browser.version : '')
    : false;
};

export const getDaysAgoInEpochSeconds = (days: number): number => {
  // Use Temporal to get current time minus days
  const currentInstant = nowInstant();
  const daysAgo = currentInstant.subtract({ hours: days * HOURS_PER_DAY });
  return Math.floor(Number(daysAgo.epochNanoseconds / BigInt(NANOSECONDS_PER_SECOND)));
};

export const getDaysAgoInEpochMilliseconds = (days: number): number => {
  // Use Temporal to get current time minus days
  const currentInstant = nowInstant();
  const daysAgo = currentInstant.subtract({ hours: days * HOURS_PER_DAY });
  return daysAgo.epochMilliseconds;
};

export const getFrom = (time: number): string => {
  const nowDate = now(); // today's date
  const endDate = parseDate(new Date(time)); // convert timestamp to date

  if (!nowDate || !endDate) {
    return '0m';
  }

  // Calculate difference in days
  const daysDiff = dateDiff(endDate, nowDate, 'days');

  // Convert to appropriate unit
  const mins = Math.abs(daysDiff * HOURS_PER_DAY * MINUTES_PER_HOUR);
  if (mins < MINUTES_THRESHOLD) return `${Math.round(mins)}m`;

  const hours = Math.abs(daysDiff * HOURS_PER_DAY);
  if (hours < HOURS_THRESHOLD) return `${Math.round(hours)}h`;

  const days = Math.abs(daysDiff);
  if (days < DAYS_THRESHOLD) return `${Math.round(days)}d`;

  const weeks = Math.round(days / DAYS_PER_WEEK);
  return `${weeks}w`;
};

export const getDurationFromDate = (date: string | null = null): string | number => {
  if (date === null) {
    return '';
  }

  // Try to parse the date - first check if it's in DD/MM/YYYY format
  let parsedDate = null;
  const dateFormatRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = date.match(dateFormatRegex);

  if (match) {
    // Convert DD/MM/YYYY to ISO format
    const [, day, month, year] = match;
    const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    parsedDate = parseDate(isoDate);
  } else {
    // Try to parse as-is
    parsedDate = parseDate(date);
  }

  if (!parsedDate) {
    return '';
  }

  const duration = dateDiff(parsedDate, now(), 'days');
  return 1 - duration;
};

export function getFormatDateFromObject(dateInput: IDate | undefined): string {
  const { YYYY, DD, MM } = dateInput ?? {};
  if (YYYY && DD && MM) {
    return `${DD}/${MM}/${normalizeToFullYear(YYYY)}`;
  }
  return '';
}

export function getDateFromString(str: string | undefined): IDate {
  if (!str) {
    return {
      YYYY: null,
      MM: null,
      DD: null,
    };
  }

  const date = parseDate(str);
  if (date) {
    const s = str.split('-');
    return {
      YYYY: s[0] || null,
      MM: s[1] || null,
      DD: s[2] || null,
    };
  }
  return {
    YYYY: null,
    MM: null,
    DD: null,
  };
}

export function getFormatDateFromStoreDate(str: string | undefined): string {
  const date = getDateFromString(str);
  return getFormatDateFromObject(date);
}

export const removeCognitoLocalStorage = (): void => {
  const win = window as Window & { localStorage: Storage };
  for (const key of Object.keys(win.localStorage)) {
    if (key.toLowerCase().includes('cognito')) {
      win.localStorage.removeItem(key);
    }
  }
};

// If this condition is true, data related to scheduled (Calendly) interviews should be hidden.
export const getDisplayScheduledInterviewsAllowed = (
  role: UserRole,
  isPathway: boolean,
  isPathwayStudent: boolean,
  relatedPathwaySettings?: IPathwayId,
): boolean => {
  const isDirectUniUser = !isPathway && UNI_ROLES.includes(role);

  if (isDirectUniUser && isPathwayStudent) {
    const isScheduledInterviewsProperty: boolean = relatedPathwaySettings?.scheduledInterviewsVisible ?? true;

    return isScheduledInterviewsProperty;
  }
  return true;
};

// If this condition is true, data related to recorded interviews should be hidden.
export const getDisplayRecordedInterviewsAllowed = (
  role: UserRole,
  isPathway: boolean,
  isPathwayStudent: boolean,
  relatedPathwaySettings?: IPathwayId,
): boolean => {
  const isDirectUniUser = !isPathway && UNI_ROLES.includes(role);

  if (isDirectUniUser && isPathwayStudent) {
    const isRecordedInterviewsProperty: boolean = relatedPathwaySettings?.recordedInterviewVisible ?? true;

    return isRecordedInterviewsProperty;
  }
  return true;
};
export {};

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export const getNotArrangedAccommodationTypeStatus = (status: string): string => {
  if (status === AccommodationTypeStatusEnum.I_DONT_KNOW) {
    return 'Unsure';
  }
  return `Considering ${status.toLocaleLowerCase()}`;
};

export const getActiveStageTab = (
  params: URLSearchParams,
  headerTab: string,
  defaultTab: string = headerTab === 'casProcess' ? DEFAULT_STAGE_TAB : DEFAULT_NOT_INVITES_STAGE_TAB,
  key: string = QueryKeys.STAGE,
): string => {
  const tabId = params.get(key) ?? defaultTab;
  return tabId;
};

export const truncateWithEllipsis = (str: string, maxLength: number): string => {
  if (str.length > maxLength) str = `${str.substring(0, maxLength)}...`;
  return str;
};

export const hashToArray = <T>(hash: { [key: string]: T }): Array<{ key: string; item: T }> => {
  const obj = hash ?? {};
  return Object.keys(obj).map(key => ({ key, item: obj[key] }));
};

export function getDateFormatFromString(str: string): string {
  const s = str && str.split ? str.split('-') : [];
  return s[2] && s[1] && s[0] ? `${s[2]}-${s[1]}-${s[0]}` : '';
}

export function titleCase(str: string): string {
  try {
    return str
      .toLowerCase()
      .split(' ')
      .map(function (word: string) {
        return word.replace(word[0], word[0]?.toUpperCase());
      })
      .join(' ');
  } catch (err: unknown) {
    if (err instanceof Error) {
      return str;
    }
  }
  return str;
}

export const getImportOffer = (
  importData: Partial<IImportData> | null,
  intakeId: string,
  offerId: string,
): IImportOffer | null => {
  return (
    importData?.offers?.find(importOffer => importOffer.intakeId === intakeId && importOffer.offerId === offerId) ??
    null
  );
};

export const checkFetchStatus = async (res: Response): Promise<Response> => {
  if (!res.ok) {
    const data = await res.text();
    throw new Error(data);
  }
  return res;
};

// If this condition is true, data related to scheduled (Calendly) interviews should be hidden.
export const getDisplayCalendlyInterviewsAllowed = (
  role: UserRole,
  isPathway: boolean,
  isPathwayStudent: boolean,
  relatedPathwaySettings?: IPathwayId,
): boolean => {
  const isDirectUniUser = !isPathway && UNI_ROLES.includes(role);

  if (isDirectUniUser && isPathwayStudent) {
    return relatedPathwaySettings?.scheduledInterviewsVisible ?? true;
  }
  return true;
};

// If this condition is true, data related to recorded interviews should be hidden.
export const getDisplayRecordedInterviewAllowed = (
  role: UserRole,
  isPathway: boolean,
  isPathwayStudent: boolean,
  relatedPathwaySettings?: IPathwayId,
): boolean => {
  const isDirectUniUser = !isPathway && UNI_ROLES.includes(role);

  if (isDirectUniUser && isPathwayStudent) {
    return relatedPathwaySettings?.recordedInterviewVisible ?? true;
  }
  return true;
};

export const isRecordedInterviewEmpty = (recordedInterviews: IRecordedInterview | null): boolean => {
  if (recordedInterviews) {
    const filteredInterviews = Object.entries(recordedInterviews).filter(item => {
      const [, interview] = item;
      return interview.systemStatus !== RecordedInterviewSystemStatus.DELETED;
    });
    return Boolean(filteredInterviews.length);
  }
  return false;
};

export const getApplicationOfferSubStatusBadgeProps = (
  status?: ApplicantOfferResponseStatus,
): { colorScheme: 'green' | 'red'; icon: IconProp } | null => {
  switch (status) {
    case ApplicantOfferResponseStatus.Accepted:
      return {
        colorScheme: 'green',
        icon: faThumbsUp,
      };
    case ApplicantOfferResponseStatus.Declined:
      return {
        colorScheme: 'red',
        icon: faThumbsDown,
      };
    default:
      return null;
  }
};

export const workflowOptions: IDropdownOption[] = Object.values(Workflows).map((workflow: string): IDropdownOption => {
  return {
    label: workflow,
    value: workflow,
  };
});
